import { useCallback, useEffect, useMemo, useState } from 'react';
import chroma from 'chroma-js';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { ActionIcon, Button, Select, Switch, Text, Tooltip } from 'rizzui';
import { CustomBadge } from '../../../components/forms/CustomBadge';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useProductTypes from '../../../hooks/api-calls/useProductTypes';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { EventsService } from '../../../services/events/eventsService';
import { EventRule, EventsApiResponse } from '../../../type/entities/events-type';
import EventModalForm, {
    CONTRACT_EVENT_OPTIONS,
    ENTITY_OPTIONS,
    EVENT_OPTIONS_BY_ENTITY,
    EXPIRATION_EVENT_OPTIONS,
    INVOICE_EVENT_OPTIONS,
    ORDER_EVENT_OPTIONS,
    STATUS_TYPE_OPTIONS
} from './EventModalForm';

const EVENT_INVOICE_UNPAID_WEEKLY_REMINDER = 'invoice.unpaid.weekly.reminder';
const EVENT_INVOICE_UNPAID_MONTHLY_EXPIRATIONS_REMINDER = 'invoice.unpaid.monthly.expirations.reminder';
const EVENT_EXPIRATION_REMITTANCE_GENERATE = 'expiration.remittance.generate';
const EVENT_EXPIRATION_REMITTANCE_CONFIRM = 'expiration.remittance.confirm';
const EVENT_CONTRACT_IN_STATUS_WITHOUT_INCIDENCE_AND_PAID_INVOICE = 'contract.in.status.without.incidence.paid.invoice';
const EVENT_CONTRACT_SIGNATURE_RESEND = 'contract.signature.resend';

const EventsList = () => {

    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const eventsService = useMemo(() => new EventsService(), []);
    const canListEvents = userCan('list_events', 'events');
    const canCreateEvents = userCan('create_events', 'events');
    const canEditEvents = userCan('edit_events', 'events');
    const canDeleteEvents = userCan('delete_events', 'events');
    const canAdminCompany = userCan('admin_company', 'companies');
    const canCreateEventSection = canCreateEvents && canAdminCompany;
    const canDeleteEventSection = canDeleteEvents && canAdminCompany;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<EventRule | null>(null);
    const [createDefaultEvent, setCreateDefaultEvent] = useState<string | null>(null);
    const [createDefaultEntity, setCreateDefaultEntity] = useState<string | null>(null);
    const [entityFilter, setEntityFilter] = useState('contracts');
    const [eventFilter, setEventFilter] = useState<string>('');
    const [productTypeFilter, setProductTypeFilter] = useState<string>('');
    const { getProductTypesList } = useProductTypes();

    const getEventOptionsByEntity = (entity: string) => EVENT_OPTIONS_BY_ENTITY[entity] || [];

    const [requestFilters, setRequestFilters] = useState<any>({
        limit: 100,
        page: 1,
        filter_filters: {
            entity: 'contracts',
        },
        filter_order: [
            { field: 'createdAt', order: 'desc' },
        ],
    });

    useEffect(() => {
        setRequestFilters((prev: any) => ({
            ...prev,
            filter_filters: {
                ...prev.filter_filters,
                entity: entityFilter,
                event: eventFilter || null,
                contract_product_type_id: productTypeFilter || null,
            },
        }));
    }, [entityFilter, eventFilter, productTypeFilter]);

    useEffect(() => {
        const availableEventValues = getEventOptionsByEntity(entityFilter).map((item) => item.value);
        if (eventFilter && !availableEventValues.includes(eventFilter)) {
            setEventFilter('');
        }
    }, [entityFilter, eventFilter]);

    const [events, loading, , refetch] = useFetch(useCallback(async () => {
        if (!canListEvents) {
            return {
                success: true,
                message: null,
                totalRegisters: 0,
                data: [],
                lastPage: 0,
                filters: requestFilters,
            } as EventsApiResponse;
        }

        const response = await eventsService.listEvents(requestFilters);
        return response.getResponseData() as EventsApiResponse;
    }, [canListEvents, requestFilters, eventsService]));

    const handleToggleStatus = useCallback(async (id: string) => {
        if (!canEditEvents) {
            toast.error('No tienes permisos para actualizar eventos');
            return;
        }

        try {
            const response = await (await eventsService.toggleEvent(id)).getResponseData();
            if (response.success) {
                toast.success('Estado del evento actualizado correctamente');
                refetch();
                return;
            }
            handleErrors(response);
        } catch (error: any) {
            handleErrors(error);
        }
    }, [canEditEvents, refetch, handleErrors, eventsService]);

    const handleDelete = useCallback(async (id: string) => {
        if (!canDeleteEventSection) {
            toast.error('No tienes permisos para eliminar eventos');
            return;
        }

        try {
            const response = await (await eventsService.deleteEvent(id)).getResponseData();
            if (response.success) {
                toast.success('Evento eliminado correctamente');
                refetch();
                return;
            }
            handleErrors(response);
        } catch (error: any) {
            handleErrors(error);
        }
    }, [canDeleteEventSection, refetch, handleErrors, eventsService]);

    const handleOpenCreateModal = () => {
        if (!canCreateEventSection) {
            toast.error('No tienes permisos para crear eventos');
            return;
        }

        setSelectedEvent(null);
        setCreateDefaultEvent(eventFilter || null);
        setCreateDefaultEntity(entityFilter);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (eventRule: EventRule) => {
        if (!canEditEvents) {
            toast.error('No tienes permisos para editar eventos');
            return;
        }

        setSelectedEvent(eventRule);
        setCreateDefaultEvent(null);
        setCreateDefaultEntity(null);
        setIsModalOpen(true);
    };

    const statusCards = useMemo(() => ([
        { key: 'eventContractStatus', label: 'Estado de contrato' },
        { key: 'eventContractProductStatus', label: 'Estado de producto' },
        { key: 'eventContractInvoiceStatus', label: 'Estado de facturación' },
        { key: 'eventContractIncidenceStatus', label: 'Estado de incidencia' },
        { key: 'eventContractCommissionStatus', label: 'Estado de comisión' },
    ]), []);

    const ALL_EVENT_OPTIONS = useMemo(() => ([
        ...CONTRACT_EVENT_OPTIONS,
        ...EXPIRATION_EVENT_OPTIONS,
        ...ORDER_EVENT_OPTIONS,
        ...INVOICE_EVENT_OPTIONS,
    ]), []);

    const getEventLabel = (eventValue: string) => {
        const fixedEventLabels: Record<string, string> = {
            'contract.client.update.credits': 'Actualización de créditos de cliente',
            'contract.cliente.update.credits': 'Actualización de créditos de cliente',
        };

        if (fixedEventLabels[eventValue]) {
            return fixedEventLabels[eventValue];
        }

        return ALL_EVENT_OPTIONS.find((item) => item.value === eventValue)?.label ?? eventValue;
    };

    const getStatusTypeLabel = (type: string | null) =>
        STATUS_TYPE_OPTIONS.find((item) => item.value === type)?.label ?? '-';

    const getEntityLabel = (entityValue: string) =>
        ENTITY_OPTIONS.find((item) => item.value === entityValue)?.label ?? entityValue;

    const getEmailTemplateUseType = (entity: string): string | null => {
        const useTypeByEntity: Record<string, string> = {
            contracts: 'contract',
            expirations: 'expiration',
        };

        return useTypeByEntity[entity] || null;
    };

    const supportsEmailTemplates = (entity: string): boolean => Boolean(getEmailTemplateUseType(entity));

    const getValidStatusColor = (status: any): string | null =>
        status?.color && chroma.valid(status.color) ? status.color : null;

    const getWeekdayLabel = (weekday: number | null) => {
        const labels: Record<number, string> = {
            1: 'Lunes',
            2: 'Martes',
            3: 'Miércoles',
            4: 'Jueves',
            5: 'Viernes',
            6: 'Sábado',
            7: 'Domingo',
        };

        if (!weekday) {
            return '-';
        }

        return labels[weekday] || '-';
    };

    const getMonthLabel = (month: number | null) => {
        const labels: Record<number, string> = {
            1: 'Enero',
            2: 'Febrero',
            3: 'Marzo',
            4: 'Abril',
            5: 'Mayo',
            6: 'Junio',
            7: 'Julio',
            8: 'Agosto',
            9: 'Septiembre',
            10: 'Octubre',
            11: 'Noviembre',
            12: 'Diciembre',
        };

        if (!month) {
            return '-';
        }

        return labels[month] || '-';
    };

    const getScheduleSummary = (eventRule: EventRule): string => {
        switch (eventRule.scheduleMode) {
            case 'custom_days':
                if (eventRule.event === EVENT_CONTRACT_SIGNATURE_RESEND) {
                    return `${eventRule.scheduleCustomDays || 0} minuto/s entre reenvíos`;
                }
                return `${eventRule.scheduleCustomDays || 0} día/s desde entrada en estado origen`;
            case 'daily':
                return `Todos los días a las ${eventRule.scheduleHour || '--:--'}`;
            case 'weekly':
                return `Cada semana (${getWeekdayLabel(eventRule.scheduleWeekday)})`;
            case 'monthly':
                return `Cada mes, día ${eventRule.scheduleDay || '-'}`;
            case 'yearly':
                return `Cada año, ${eventRule.scheduleDay || '-'} de ${getMonthLabel(eventRule.scheduleMonth)}`;
            default:
                return 'Sin definir';
        }
    };

    const renderStatusBox = (status: any, label: string) => {
        const statusName = status?.name || null;
        const statusColor = getValidStatusColor(status);

        if (!statusName) {
            return (
                <div className="rounded-xl border border-dashed border-gray-300 bg-white p-3" key={label}>
                    <Text className="text-[11px] uppercase tracking-wide text-gray-500">
                        {label}
                    </Text>
                    <Text className="mt-2 text-sm font-medium text-gray-400">
                        Sin definir
                    </Text>
                </div>
            );
        }

        const cardStyle = statusColor
            ? {
                backgroundColor: chroma(statusColor).alpha(0.08).css(),
                borderColor: chroma(statusColor).alpha(0.35).css(),
            }
            : undefined;

        return (
            <div className="rounded-xl border bg-white p-3 shadow-sm" style={cardStyle} key={label}>
                <Text className="text-[11px] uppercase tracking-wide text-gray-500">
                    {label}
                </Text>

                <div className="mt-2 flex items-center gap-2">
                    <span
                        className="h-2.5 w-2.5 rounded-full shadow-sm"
                        style={{ backgroundColor: statusColor || '#94a3b8' }}
                    />
                    {statusColor ? (
                        <CustomBadge customColor={statusColor} size="sm" rounded="md" className="max-w-full">
                            <span className="truncate">{statusName}</span>
                        </CustomBadge>
                    ) : (
                        <span className="inline-flex max-w-full items-center rounded-md bg-gray-200 px-2 py-1 text-xs font-semibold text-gray-700">
                            <span className="truncate">{statusName}</span>
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const renderChangeStatusCard = (
        positionLabel: string,
        statusType: string | null,
        status: any,
        destinationStatusMode: string = 'specific'
    ) => {
        const statusName = status?.name || null;
        const statusColor = getValidStatusColor(status);
        const isPreviousDestinationMode = positionLabel === 'Destino' && destinationStatusMode === 'previous';

        const cardStyle = statusColor
            ? {
                backgroundColor: chroma(statusColor).alpha(0.08).css(),
                borderColor: chroma(statusColor).alpha(0.35).css(),
            }
            : (isPreviousDestinationMode
                ? {
                    backgroundColor: '#eff6ff',
                    borderColor: '#93c5fd',
                }
                : undefined);

        return (
            <div className="rounded-xl border bg-white p-3 shadow-sm" style={cardStyle}>
                <Text className="text-[11px] uppercase tracking-wide text-gray-500">
                    {positionLabel}
                </Text>
                <Text className="mt-1 text-xs font-semibold text-gray-600">
                    {getStatusTypeLabel(statusType)}
                </Text>

                {isPreviousDestinationMode ? (
                    <Text className="mt-2 text-sm font-semibold text-blue-900">
                        Estado anterior (si existe)
                    </Text>
                ) : statusName ? (
                    <div className="mt-2 flex items-center gap-2">
                        <span
                            className="h-2.5 w-2.5 rounded-full shadow-sm"
                            style={{ backgroundColor: statusColor || '#94a3b8' }}
                        />
                        {statusColor ? (
                            <CustomBadge customColor={statusColor} size="sm" rounded="md" className="max-w-full">
                                <span className="truncate">{statusName}</span>
                            </CustomBadge>
                        ) : (
                            <span className="inline-flex max-w-full items-center rounded-md bg-gray-200 px-2 py-1 text-xs font-semibold text-gray-700">
                                <span className="truncate">{statusName}</span>
                            </span>
                        )}
                    </div>
                ) : (
                    <Text className="mt-2 text-sm font-medium text-gray-400">
                        Sin definir
                    </Text>
                )}
            </div>
        );
    };

    return (
        <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-6">
            {!canListEvents && (
                <div className="xl:col-span-2 rounded-2xl border border-dashed border-gray-300 p-8 text-center bg-white">
                    <Text className="text-gray-500">
                        No tienes permisos para visualizar eventos.
                    </Text>
                </div>
            )}

            {canListEvents && (
                <>
            <div className="rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 p-4 h-fit">
                <Text className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-4">
                    Filtros
                </Text>

                <Select
                    label="Entidad"
                    options={ENTITY_OPTIONS}
                    value={ENTITY_OPTIONS.find((item) => item.value === entityFilter) || null}
                    onChange={(option: any) => setEntityFilter(option?.value || 'contracts')}
                />

                <div className="mt-4">
                    <Select
                        label="Tipo de producto"
                        options={getProductTypesList()}
                        value={getProductTypesList().find((item: any) => item.value === productTypeFilter) || null}
                        onChange={(option: any) => setProductTypeFilter(option?.value || '')}
                        placeholder="Todos"
                        clearable
                        onClear={() => setProductTypeFilter('')}
                    />
                </div>

                <div className="mt-4 space-y-2">
                    <Button
                        variant={eventFilter === '' ? 'solid' : 'outline'}
                        className="h-auto w-full !justify-start !text-left whitespace-normal break-words py-2 leading-snug"
                        onClick={() => setEventFilter('')}
                    >
                        <span className="block w-full text-left whitespace-normal break-words leading-snug">
                            Todos los eventos
                        </span>
                    </Button>
                    {getEventOptionsByEntity(entityFilter).map((eventOption) => (
                        <Button
                            key={eventOption.value}
                            variant={eventFilter === eventOption.value ? 'solid' : 'outline'}
                            className="h-auto w-full !justify-start !text-left whitespace-normal break-words py-2 leading-snug"
                            onClick={() => setEventFilter(eventOption.value)}
                        >
                            <span className="block w-full text-left whitespace-normal break-words leading-snug">
                                {eventOption.label}
                            </span>
                        </Button>
                    ))}
                </div>
            </div>

            <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <Text className="text-sm text-gray-500">
                        {events?.totalRegisters || 0} evento/s configurado/s
                    </Text>

                    {canCreateEventSection && (
                        <Button onClick={handleOpenCreateModal}>
                            Crear evento
                        </Button>
                    )}
                </div>

                {loading && (
                    <Text className="text-sm text-gray-500">Cargando eventos...</Text>
                )}

                {!loading && events?.data?.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-gray-300 p-8 text-center bg-white">
                        <Text className="text-gray-500">
                            No hay eventos configurados para los filtros seleccionados.
                        </Text>
                    </div>
                )}

                {!loading && events?.data?.map((eventRule: EventRule) => {
                    const isContractDocumentUploadEvent = eventRule.event === 'contract.document.upload';
                    const isContractInStatusWithoutIncidenceAndPaidInvoiceEvent =
                        eventRule.event === EVENT_CONTRACT_IN_STATUS_WITHOUT_INCIDENCE_AND_PAID_INVOICE;
                    const contractTriggerDocumentTypeNames = (eventRule.contractDocumentTypes || []).length > 0
                        ? eventRule.contractDocumentTypes.map((item) => item.name).join(', ')
                        : (eventRule.contractDocumentType?.name || 'Sin definir');
                    return (
                    <div key={eventRule.id} className="rounded-2xl border-2 border-gray-300 bg-gray-50 p-5">
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                            <div>
                                <Text className="text-xs uppercase tracking-wide text-gray-500">
                                    {getEntityLabel(eventRule.entity)}
                                </Text>
                                <Text className="text-lg font-semibold text-gray-900 break-words">
                                    {eventRule.name}
                                </Text>
                                {eventRule.description && (
                                    <Text className="mt-1 text-sm text-gray-600">
                                        {eventRule.description}
                                    </Text>
                                )}
                                <Text className="mt-1 text-sm text-gray-600 break-words">
                                    <span className={"font-bold"}>Evento:</span> {getEventLabel(eventRule.event)}
                                </Text>
                            </div>

                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <div className="rounded-md border border-dashed border-gray-400 px-3 py-2 bg-white">
                                    <Text className="text-[11px] uppercase tracking-wide text-gray-500">
                                        Tipo de producto
                                    </Text>
                                    <Text className="text-sm font-semibold text-gray-900">
                                        {['contracts', 'orders'].includes(eventRule.entity)
                                            ? (eventRule.contractProductType?.name || 'Sin definir (Todos)')
                                            : 'No aplica'}
                                    </Text>
                                </div>

                                {isContractDocumentUploadEvent && (
                                    <div className="rounded-md border border-dashed border-gray-400 px-3 py-2 bg-white min-w-[210px]">
                                        <Text className="text-[11px] uppercase tracking-wide text-gray-500">
                                            Tipos de documento disparador
                                        </Text>
                                        <Text className="text-sm font-semibold text-gray-900">
                                            {contractTriggerDocumentTypeNames}
                                        </Text>
                                    </div>
                                )}

                                <div className="rounded-md border border-dashed border-gray-400 px-3 py-2 bg-white min-w-[210px]">
                                    <Text className="text-[11px] uppercase tracking-wide text-gray-500">
                                        Comunicación
                                    </Text>
                                    <Text className="text-sm font-semibold text-gray-900">
                                        {!supportsEmailTemplates(eventRule.entity)
                                            ? 'No aplica'
                                            : ['contract.register', EVENT_CONTRACT_SIGNATURE_RESEND].includes(eventRule.event)
                                            ? `SMS: ${eventRule.sendSms ? 'Sí' : 'No'} · Email: ${eventRule.sendEmail ? 'Sí' : 'No'}`
                                            : `Email: ${eventRule.sendEmail ? 'Sí' : 'No'}`}
                                    </Text>
                                    {supportsEmailTemplates(eventRule.entity) && eventRule.sendEmail && (
                                        <>
                                            <Text className="text-xs text-gray-600">
                                                Plantilla: {(eventRule.emailTemplates || []).length > 0
                                                    ? eventRule.emailTemplates.map((item) => item.name).join(', ')
                                                    : (eventRule.emailTemplate?.name || 'Sin definir')}
                                            </Text>
                                            <Text className="text-xs text-gray-600">
                                                CC gestoría: {eventRule.sendEmailConsultancyCopy ? 'Sí' : 'No'}
                                            </Text>
                                            <Text className="text-xs text-gray-600">
                                                Adjuntos por tipo: {(eventRule.emailAttachmentDocumentTypes || []).length > 0
                                                    ? eventRule.emailAttachmentDocumentTypes.map((item) => item.name).join(', ')
                                                    : 'Sin configurar'}
                                            </Text>
                                        </>
                                    )}
                                </div>

                                {canEditEvents && (
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={eventRule.isActive}
                                            onChange={() => handleToggleStatus(eventRule.id)}
                                        />
                                        <Text className={`text-xs font-semibold ${eventRule.isActive ? 'text-green-600' : 'text-gray-500'}`}>
                                            {eventRule.isActive ? 'Activo' : 'Inactivo'}
                                        </Text>
                                    </div>
                                )}

                                {(canEditEvents || canDeleteEventSection) && (
                                    <div className="flex items-center gap-2">
                                        {canEditEvents && (
                                            <Tooltip
                                                size="sm"
                                                content={'Editar evento'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <ActionIcon
                                                    as="span"
                                                    size="sm"
                                                    variant="outline"
                                                    className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                                    onClick={() => handleOpenEditModal(eventRule)}
                                                >
                                                    <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}

                                        {canDeleteEventSection && (
                                            <DeletePopover
                                                title={`Eliminar evento`}
                                                description={`¿Estás seguro de que deseas eliminar este evento?`}
                                                onDelete={() => handleDelete(eventRule.id)}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {(
                            (![
                                'contract.change.status',
                                'expiration.management.change.status',
                                EVENT_INVOICE_UNPAID_WEEKLY_REMINDER,
                                EVENT_INVOICE_UNPAID_MONTHLY_EXPIRATIONS_REMINDER,
                                EVENT_CONTRACT_SIGNATURE_RESEND,
                            ].includes(eventRule.event) && eventRule.entity === 'contracts')
                            || (eventRule.entity === 'orders' && eventRule.event === 'order.change.status')
                        ) && (
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                                {statusCards.map((statusCard: any) =>
                                    renderStatusBox((eventRule as any)[statusCard.key], statusCard.label)
                                )}
                            </div>
                        )}

                        {eventRule.entity === 'orders' && eventRule.event === 'order.change.status' && (
                            <div className="mt-3 rounded-xl border border-dashed border-gray-300 bg-white p-3">
                                <Text className="text-[11px] uppercase tracking-wide text-gray-500">
                                    Estado de pedido disparador
                                </Text>
                                <Text className="mt-1 text-sm font-semibold text-gray-900">
                                    {eventRule.originStatus?.name || 'Sin definir'}
                                </Text>
                            </div>
                        )}

                        {['contract.course.start.today', 'contract.course.end.today'].includes(eventRule.event) && (
                            <div className="mt-3 rounded-xl border border-dashed border-gray-300 bg-white p-3">
                                <Text className="text-[11px] uppercase tracking-wide text-gray-500">
                                    Estado de producto previo requerido
                                </Text>
                                <Text className="mt-1 text-sm font-semibold text-gray-900">
                                    {eventRule.originStatus?.name || 'Sin definir'}
                                </Text>
                            </div>
                        )}

                        {['contract.course.start.today', 'contract.course.end.today'].includes(eventRule.event) && (
                            <div className="mt-3 rounded-xl border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                                <Text className="text-[11px] uppercase tracking-wide text-blue-700">
                                    Frecuencia de comprobación
                                </Text>
                                <Text className="text-sm font-semibold text-blue-900">
                                    {getScheduleSummary(eventRule)}
                                </Text>
                            </div>
                        )}

                        {['contract.change.status', 'expiration.management.change.status'].includes(eventRule.event) && (
                            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                <Text className="text-xs uppercase tracking-wide text-gray-500">
                                    Cambio de estado
                                </Text>

                                <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
                                    {renderChangeStatusCard('Origen', eventRule.originStatusType, eventRule.originStatus)}

                                    <div className="hidden md:flex items-center justify-center">
                                        <span className="rounded-full border border-gray-300 bg-gray-50 px-3 py-1 text-sm font-semibold text-gray-600">
                                            {'->'}
                                        </span>
                                    </div>

                                    <div className="md:hidden flex items-center justify-center">
                                        <span className="rounded-full border border-gray-300 bg-gray-50 px-3 py-1 text-sm font-semibold text-gray-600">
                                            v
                                        </span>
                                    </div>

                                    {renderChangeStatusCard('Destino', eventRule.destinationStatusType, eventRule.destinationStatus, eventRule.destinationStatusMode)}
                                </div>

                                {eventRule.event === 'expiration.management.change.status' && (
                                    <div className="mt-3 rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                                        <Text className="text-[11px] uppercase tracking-wide text-blue-700">
                                            Programación
                                        </Text>
                                        <Text className="text-sm font-semibold text-blue-900">
                                            {getScheduleSummary(eventRule)}
                                        </Text>
                                    </div>
                                )}
                            </div>
                        )}

                        {eventRule.event === EVENT_INVOICE_UNPAID_WEEKLY_REMINDER && (
                            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                <Text className="text-xs uppercase tracking-wide text-gray-500">
                                    Recordatorio de factura impagada
                                </Text>

                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                                    {renderChangeStatusCard('Estado monitorizado', eventRule.originStatusType, eventRule.originStatus)}

                                    <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                                        <Text className="text-[11px] uppercase tracking-wide text-blue-700">
                                            Programación
                                        </Text>
                                        <Text className="text-sm font-semibold text-blue-900">
                                            {getScheduleSummary(eventRule)}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        )}

                        {eventRule.event === EVENT_INVOICE_UNPAID_MONTHLY_EXPIRATIONS_REMINDER && (
                            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                <Text className="text-xs uppercase tracking-wide text-gray-500">
                                    Recordatorio mensual de vencimientos próximo a cobro
                                </Text>

                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                                    <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                                        <Text className="text-[11px] uppercase tracking-wide text-blue-700">
                                            Programación
                                        </Text>
                                        <Text className="text-sm font-semibold text-blue-900">
                                            {getScheduleSummary(eventRule)}
                                        </Text>
                                    </div>

                                    <div className="rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2">
                                        <Text className="text-[11px] uppercase tracking-wide text-gray-500">
                                            Alcance
                                        </Text>
                                        <Text className="text-sm font-semibold text-gray-900">
                                            Facturas cliente con vencimientos próximos a cobro (mes actual).
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        )}

                        {isContractInStatusWithoutIncidenceAndPaidInvoiceEvent && (
                            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                <Text className="text-xs uppercase tracking-wide text-gray-500">
                                    Contrato en estado durante X días
                                </Text>

                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                                    {renderChangeStatusCard('Estado contrato monitorizado', 'contract', eventRule.originStatus)}
                                    {renderChangeStatusCard('Estado producto destino', 'contract_product', eventRule.eventContractProductStatus)}

                                    <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                                        <Text className="text-[11px] uppercase tracking-wide text-blue-700">
                                            Días en estado
                                        </Text>
                                        <Text className="text-sm font-semibold text-blue-900">
                                            {eventRule.scheduleCustomDays || 0} día/s
                                        </Text>
                                    </div>

                                    <div className="rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2">
                                        <Text className="text-[11px] uppercase tracking-wide text-gray-500">
                                            Condiciones adicionales
                                        </Text>
                                        <Text className="text-sm font-semibold text-gray-900">
                                            Facturación satisfactoria y sin incidencia activa (o satisfactoria).
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        )}

                        {eventRule.event === EVENT_CONTRACT_SIGNATURE_RESEND && (
                            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                <Text className="text-xs uppercase tracking-wide text-gray-500">
                                    Reenvío de firma de contrato
                                </Text>

                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                                    <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                                        <Text className="text-[11px] uppercase tracking-wide text-blue-700">
                                            Intervalo mínimo
                                        </Text>
                                        <Text className="text-sm font-semibold text-blue-900">
                                            {eventRule.scheduleCustomDays || 0} minuto/s
                                        </Text>
                                    </div>

                                    <div className="rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2">
                                        <Text className="text-[11px] uppercase tracking-wide text-gray-500">
                                            Canales
                                        </Text>
                                        <Text className="text-sm font-semibold text-gray-900">
                                            SMS: {eventRule.sendSms ? 'Sí' : 'No'} · Email: {eventRule.sendEmail ? 'Sí' : 'No'}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        )}

                        {[EVENT_EXPIRATION_REMITTANCE_GENERATE, EVENT_EXPIRATION_REMITTANCE_CONFIRM].includes(eventRule.event) && (
                            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                <Text className="text-xs uppercase tracking-wide text-gray-500">
                                    {eventRule.event === EVENT_EXPIRATION_REMITTANCE_GENERATE
                                        ? 'Estado de vencimiento al generar remesa'
                                        : 'Estado de vencimiento al confirmar remesa'}
                                </Text>

                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                                    {renderChangeStatusCard('Destino', 'expiration', eventRule.destinationStatus)}

                                    <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                                        <Text className="text-[11px] uppercase tracking-wide text-blue-700">
                                            Disparador
                                        </Text>
                                        <Text className="text-sm font-semibold text-blue-900">
                                            {eventRule.event === EVENT_EXPIRATION_REMITTANCE_GENERATE
                                                ? 'Generación de remesa'
                                                : 'Confirmación de remesa'}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    );
                })}
            </div>
                </>
            )}

            <EventModalForm
                data={selectedEvent}
                initialEvent={createDefaultEvent}
                initialEntity={createDefaultEntity}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                refetch={refetch}
            />
        </div>
    );
};

export default EventsList;
